<script setup lang="ts">
import { computed } from 'vue';
import { useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { Lock } from '@lucide/vue';
import { cn } from '@/lib/utils';
import BrandLogo from '@app/presentation/components/shared/BrandLogo.vue';
import PaperStrip from '@app/presentation/components/paper/PaperStrip.vue';
import LanguageToggle from '@app/presentation/components/shared/LanguageToggle.vue';

import ProfileDropdown from '@app/presentation/components/shared/ProfileDropdown.vue';
import { Button } from '@/components/ui/button';
import { useAuth, useHydrated } from '@app/composables';
import { getVisibleNavItems, authCtaItem } from '@/config/navigation';
import { layoutConfig } from '@/config/layout';

withDefaults(defineProps<{
  // When true, render ONLY the in-flow bottom-tabs bar (used inside the desktop
  // phone frame). When false (default), render the unchanged mobile/desktop nav.
  compact?: boolean;
}>(), {
  compact: false,
});

const route = useRoute();
const { t } = useI18n();
const { isAuthenticated, isAdmin, isOwner } = useAuth();
const isHydrated = useHydrated();

const { mobileLayout } = layoutConfig;

// Hide nav entirely on immersive ('bare') and full-width admin/demo ('plain') routes.
const hideNav = computed(() => route.meta.chrome === 'bare' || route.meta.chrome === 'plain');

// Gate debug language toggle behind VITE_DEBUG (mirrors DebugPanel.vue / main.ts pattern)
const showDebugLanguageToggle = computed(() => import.meta.env.VITE_DEBUG === 'true');

// Hydration-safe auth/platform: the pre-rendered (SSR) HTML is always the guest
// + web nav. Render that exact structure on the client's first render too, then
// reactively reflect the real values after mount so Vue hydrates in place
// instead of replacing the subtree (which cascades into the marketing hero and
// replays its entrance animations). The post-hydration switch is a normal patch.
const authed = computed(() => isHydrated.value && isAuthenticated.value);

// Nav item lists are reactive so they reflect the real auth state after
// hydration. (All current nav items are 'public', so the list itself is stable;
// only showLoginCta / the guest-locked conditionals change structurally.)
const visibleNav = computed(() =>
  getVisibleNavItems({
    isAuthenticated: authed.value,
    isAdmin: isHydrated.value && isAdmin.value,
    isOwner: isHydrated.value && isOwner.value,
  })
);
const mainNav = computed(() => visibleNav.value.mainNav);
const mobileNav = computed(() => visibleNav.value.mobileNav);
const showLoginCta = computed(() => visibleNav.value.showLoginCta);

const isActive = (href: string) => {
  if (href === '/') return route.path === '/';
  return route.path.startsWith(href);
};
</script>

<template>
  <!-- Bottom tabs — a torn paper strip (raised popover stock, so it separates
       from the card-stock content scrolling under it; torn top edge with a lit
       fibre line + cast shade, see PaperStrip). Two placements, one bar:
       - compact (desktop phone frame): in-flow at the bottom of its flex parent;
       - default (mobile web / Telegram): fixed to the viewport, hidden ≥ md.
       Honors hideNav and mobileLayout. The active tab is a cream paper tag
       placed behind her chalk icon (icon inked dark on the tag). -->
  <nav
    v-if="mobileLayout === 'bottom-tabs' && !hideNav"
    :class="compact
      ? 'relative isolate shrink-0'
      : 'fixed bottom-0 left-0 right-0 z-50 md:hidden pb-[var(--tab-bar-inset)]'"
  >
    <PaperStrip edge="top" stock="popover" />
    <!-- The fixed bar's size comes from AppShell's --tab-bar-row / --tab-bar-inset
         (the same vars <main> reserves), so tab pages always clear it. The
         compact frame bar is in flow and stays content-sized. -->
    <div
      :class="cn('grid gap-1 px-4', !compact && 'h-[var(--tab-bar-row)]')"
      :style="{ gridTemplateColumns: `repeat(${mobileNav.length + (showLoginCta ? 1 : 0)}, 1fr)` }"
    >
      <RouterLink
        v-for="(item, index) in mobileNav"
        :key="item.id"
        :to="item.lockedForGuests && !authed ? '/login' : item.href"
        :class="cn(
          'group flex flex-col items-center justify-center gap-1.5 rounded-[var(--paper-radius-xs)] py-2.5 transition-colors',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-paper-stock/50',
          isActive(item.href) ? 'text-foreground' : 'text-muted-foreground hover:text-foreground'
        )"
      >
        <!-- Guest-locked: only the icon is dimmed; the label keeps full AA ink
             and the Lock glyph names the state. -->
        <span
          :class="cn(
            'relative grid h-8 w-8 shrink-0 place-items-center',
            item.lockedForGuests && !authed && 'opacity-75'
          )"
        >
          <!-- Active: a cream tag tucked behind the icon, tilted ±1.5° by
               position, "placed" on activation (see .tab-tag). -->
          <span
            v-if="isActive(item.href)"
            aria-hidden="true"
            class="tab-tag paper-tag absolute -inset-x-1.5 -inset-y-1 rounded-[var(--paper-radius-xs)] min-[360px]:-inset-x-2.5"
            :style="{ '--tab-tilt': index % 2 === 0 ? '-1.5deg' : '1.5deg' }"
          />
          <img
            v-if="item.iconUrl"
            :src="item.iconUrl"
            alt=""
            :class="cn(
              'relative h-8 w-8 object-contain transition-opacity',
              isActive(item.href) ? 'brightness-0 opacity-80' : 'brightness-0 invert opacity-60 group-hover:opacity-80'
            )"
          />
          <component
            :is="item.icon"
            v-else-if="item.icon"
            :class="cn(
              'relative h-8 w-8',
              isActive(item.href) ? 'text-paper-stock-ink' : 'text-muted-foreground'
            )"
          />
        </span>
        <span class="text-xs font-medium flex items-center gap-0.5 whitespace-nowrap">
          {{ t(item.labelKey) }}
          <Lock v-if="item.lockedForGuests && !authed" class="w-2.5 h-2.5" />
        </span>
      </RouterLink>
      <RouterLink
        v-if="showLoginCta && authCtaItem"
        :to="authCtaItem.href"
        class="group flex flex-col items-center justify-center gap-1.5 rounded-[var(--paper-radius-xs)] py-2.5 text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-paper-stock/50"
      >
        <img
          v-if="authCtaItem.iconUrl"
          :src="authCtaItem.iconUrl"
          alt=""
          class="h-8 w-8 shrink-0 object-contain brightness-0 invert opacity-60"
        />
        <component :is="authCtaItem.icon" v-else-if="authCtaItem.icon" class="h-8 w-8 shrink-0" />
        <span class="text-xs font-medium whitespace-nowrap">{{ t(authCtaItem.labelKey) }}</span>
      </RouterLink>
    </div>
  </nav>

  <template v-if="!compact">
  <!-- Desktop: Top header with nav (shown on desktop unless hidden by route) -->
  <!-- Paper strip with a torn bottom edge (PaperStrip), solid stock — no blur. -->
  <header v-if="!hideNav" class="fixed top-0 left-0 right-0 z-50 hidden md:block">
    <PaperStrip edge="bottom" wide />
    <div class="max-w-3xl lg:max-w-5xl xl:max-w-6xl 2xl:max-w-7xl mx-auto px-4 lg:px-6">
      <div class="flex items-center justify-between h-14">
        <!-- Left: Logo + Nav tabs -->
        <div class="flex items-center gap-6">
          <BrandLogo size="md" :show-text="true" />
          <nav class="flex items-center gap-1">
            <RouterLink
              v-for="item in mainNav"
              :key="item.id"
              :to="item.lockedForGuests && !authed ? '/login' : item.href"
              :class="cn(
                'flex items-center gap-2 px-4 py-2 rounded-[var(--paper-radius-xs)] text-sm font-medium transition-colors whitespace-nowrap',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-paper-stock/50',
                isActive(item.href)
                  ? 'paper-tag shadow-[0_1px_0_rgb(4_3_18/0.5),0_4px_8px_-4px_var(--paper-shade)]'
                  : 'text-muted-foreground hover:text-foreground hover:bg-foreground/8'
              )"
            >
              <!-- Guest-locked: dim the icon only (label stays AA, Lock glyph shows it). -->
              <img
                v-if="item.iconUrl"
                :src="item.iconUrl"
                alt=""
                :class="cn(
                  'w-4 h-4',
                  isActive(item.href) ? 'brightness-0 opacity-80' : 'brightness-0 invert opacity-70',
                  item.lockedForGuests && !authed && 'opacity-50'
                )"
              />
              <component
                :is="item.icon"
                v-else-if="item.icon"
                :class="cn('w-4 h-4', item.lockedForGuests && !authed && 'opacity-75')"
              />
              <span class="flex items-center gap-1">
                {{ t(item.labelKey) }}
                <Lock v-if="item.lockedForGuests && !authed" class="w-2.5 h-2.5" />
              </span>
            </RouterLink>
          </nav>
        </div>

        <!-- Right: Controls -->
        <div class="flex items-center gap-2">
          <LanguageToggle v-if="showDebugLanguageToggle" />
          <Button v-if="showLoginCta && authCtaItem" as-child>
            <RouterLink :to="authCtaItem.href">
              <img v-if="authCtaItem.iconUrl" :src="authCtaItem.iconUrl" alt="" class="w-4 h-4 mr-2" />
              <component :is="authCtaItem.icon" v-else-if="authCtaItem.icon" class="w-4 h-4 mr-2" />
              {{ t(authCtaItem.labelKey) }}
            </RouterLink>
          </Button>
          <ProfileDropdown v-if="authed" />
        </div>
      </div>
    </div>
  </header>

  <!-- Spacer to prevent content from being hidden under fixed header -->
  <div v-if="!hideNav" class="hidden md:block h-14" />
  </template>
</template>

<style scoped>
/* Active tab tag: a cream slip laid behind the icon, tilted by position.
   "Place-in" on activation — it drops the last few px and settles at its tilt
   (ease-out-quint, no overshoot). Off under reduced motion: it is just there. */
.tab-tag {
  rotate: var(--tab-tilt, 0deg);
  box-shadow: 0 1px 0 rgb(4 3 18 / 0.5), 0 4px 8px -4px var(--paper-shade);
  animation: tab-tag-place 220ms cubic-bezier(0.22, 1, 0.36, 1) backwards;
}

@keyframes tab-tag-place {
  from {
    opacity: 0;
    translate: 0 -5px;
    rotate: 0deg;
  }
}

@media (prefers-reduced-motion: reduce) {
  .tab-tag {
    animation: none;
  }
}
</style>
